import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { Badge, CardHead, Empty, ErrorBanner, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Switch } from '../../components/ui/Form.js';
import { DISPOSITION_GROUPS, DISPOSITION_GROUP_LABELS,
  type DispositionCode, type DispositionGroup, type PauseReasonRow } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * The two catalogues the workspace works from: why somebody is not ready, and what a call was
 * (API dispositions.ts, migrations 0107 and 0108).
 *
 * One screen, because they are the same shape and the same decision -- the words this account
 * uses -- and because a person setting up a floor sets both in one sitting. What differs is the
 * flag each carries: a pause says whether the time counts as work, a disposition says which
 * group it sits under -- positive, neutral or negative -- and whether it needs a note.
 *
 * A code is RETIRED rather than deleted once it has been used. Agents stop seeing it; last
 * month's report keeps its label, which is the difference between history and a row of blanks.
 */

/** A label somebody types, as a code: "Tea break" becomes tea_break. */
const codeFrom = (label: string): string =>
  label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 31);

export function AgentCodesTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const mayEdit = can('queue:agent:manage');
  return (
    <>
      <Catalogue kind="pause" tenantId={tenantId} mayEdit={mayEdit} />
      <div style={{ height: 16 }} />
      <Catalogue kind="disposition" tenantId={tenantId} mayEdit={mayEdit} />
      <div style={{ height: 16 }} />
      <FloorRules tenantId={tenantId} mayEdit={mayEdit} />
    </>
  );
}

/** How a shift behaves, as opposed to what its words are (0110). */
function FloorRules({ tenantId, mayEdit }: { tenantId: string; mayEdit: boolean }) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/agent-settings`;
  const key = ['agent-settings', tenantId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<{ require_wrap_up: boolean }>(base) });
  const save = useMutation({
    mutationFn: (v: boolean) => api.put(base, { require_wrap_up: v }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: key }); },
  });

  return (
    <div className="arc-card">
      <CardHead title="How wrap-up works"
        sub="The account's answer, where a queue and an agent have not given their own" />
      <div className="arc-card-body">
        {save.error && <ErrorBanner error={save.error} />}
        <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Switch id="require-wrap-up" checked={q.data?.require_wrap_up ?? false}
            onChange={(v) => save.mutate(v)} />
          <label htmlFor="require-wrap-up" style={{ fontSize: 12.5, cursor: 'pointer' }}>
            A call must be marked before the agent goes ready
            <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
              The call ends into wrap-up and the agent leaves it by saying what the call was. They
              can still take a break from there, and the queue stops offering them calls until
              they are ready again.
              <br />
              This is the fallback. A queue answers for its own calls (Queues), and an agent can
              be given their own answer on their extension — most specific wins.
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

type Kind = 'pause' | 'disposition';

/** What a disposition does to a campaign contact (0128), in the words an admin chooses by. */
const EFFECT_LABELS: Record<DispositionCode['effect'], string> = {
  final: 'Reached, stop calling',
  retry: 'Not reached, try again',
  next_number: 'Wrong number, try their next',
};
type Row = PauseReasonRow & Partial<DispositionCode>;

const PATHS: Record<Kind, string> = {
  pause: 'pause-reasons',
  disposition: 'disposition-codes',
};

function Catalogue({ kind, tenantId, mayEdit }: { kind: Kind; tenantId: string; mayEdit: boolean }) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/${PATHS[kind]}`;
  const key = [PATHS[kind], tenantId];
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [flag, setFlag] = useState(false);
  const [group, setGroup] = useState<DispositionGroup>('neutral');

  const list = useQuery({ queryKey: key, queryFn: () => api.get<{ data: Row[] }>(base) });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: key });
    // The workspace reads the same catalogues.
    void qc.invalidateQueries({ queryKey: ['disposition-codes', tenantId] });
  };

  const create = useMutation({
    mutationFn: (v: { label: string; flag: boolean; group: DispositionGroup }) => api.post(base, kind === 'pause'
      ? { code: codeFrom(v.label), label: v.label.trim(), productive: v.flag }
      : { code: codeFrom(v.label), label: v.label.trim(), requires_note: v.flag, kind: v.group }),
    onSuccess: () => { setAdding(false); setLabel(''); setFlag(false); invalidate(); },
  });

  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.patch(`${base}/${v.id}`, v.body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: invalidate,
  });

  const order: Record<string, number> = { positive: 0, neutral: 1, negative: 2 };
  const rows = [...(list.data?.data ?? [])].sort((a, b) => (kind === 'disposition'
    ? (order[a.kind ?? 'neutral']! - order[b.kind ?? 'neutral']!) || a.position - b.position
    : a.position - b.position) || a.label.localeCompare(b.label));
  const title = kind === 'pause' ? 'Pause reasons' : 'Dispositions';
  const sub = kind === 'pause'
    ? 'Why somebody is not ready, and whether that time counts as work'
    : 'What a call was, as your agents mark it when it ends';
  const flagLabel = kind === 'pause' ? 'Counts as work' : 'Needs a note';
  const codeTaken = rows.some((r) => r.code === codeFrom(label));

  return (
    <div className="arc-card">
      <CardHead title={title} sub={sub} action={mayEdit ? (
        <button type="button" className="arc-btn arc-btn-sm arc-btn-primary"
          onClick={() => setAdding((v) => !v)}>
          <Icon name={adding ? 'close' : 'plus'} size={13} />
          {adding ? 'Cancel' : 'Add'}
        </button>
      ) : undefined} />

      {(create.error || patch.error || remove.error) && (
        <div className="arc-card-body">
          <ErrorBanner error={create.error ?? patch.error ?? remove.error} />
        </div>
      )}

      {adding && (
        <form className="arc-card-body arc-row" style={{ gap: 8, flexWrap: 'wrap' }}
          onSubmit={(e) => { e.preventDefault(); if (label.trim() && !codeTaken) create.mutate({ label, flag, group }); }}>
          <input className="arc-input" style={{ maxWidth: 240 }} placeholder={kind === 'pause' ? 'Tea break' : 'Escalated'}
            aria-label={`New ${title.toLowerCase()}`} value={label}
            onChange={(e) => setLabel(e.target.value)} autoFocus />
          {kind === 'disposition' && (
            <select className="arc-select" style={{ maxWidth: 140 }} aria-label="Group"
              value={group} onChange={(e) => setGroup(e.target.value as DispositionGroup)}>
              {DISPOSITION_GROUPS.map((g) => (
                <option key={g} value={g}>{DISPOSITION_GROUP_LABELS[g]}</option>
              ))}
            </select>
          )}
          <label className="arc-row" style={{ gap: 8, fontSize: 12.5 }}>
            <Switch id={`${kind}-flag`} checked={flag} onChange={setFlag} />
            {flagLabel}
          </label>
          <button type="submit" className="arc-btn arc-btn-sm arc-btn-primary"
            disabled={!label.trim() || codeTaken || create.isPending}>
            Add
          </button>
          {/* The code is derived, not typed: it is what reports group on, and a person naming a
              reason should not have to think about it. */}
          {label.trim() && (
            <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>
              {codeTaken ? 'that one already exists' : codeFrom(label) || '—'}
            </span>
          )}
        </form>
      )}

      <div className="arc-table-wrap">
        <table className="arc-table">
          <colgroup>
            <col />
            {kind === 'disposition' && <col style={{ width: 140 }} />}
            {kind === 'disposition' && <col style={{ width: 210 }} />}
            <col style={{ width: 150 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Name</th>
              {kind === 'disposition' && <th>Group</th>}
              {kind === 'disposition' && <th>In a campaign</th>}
              <th>{flagLabel}</th>
              <th>Shown to agents</th>
              <th />
            </tr>
          </thead>
          {list.data === undefined ? <TableSkeleton cols={kind === 'disposition' ? 6 : 4} /> : (
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="arc-stack" style={{ gap: 1 }}>
                      <span style={{ fontWeight: 550 }}>{r.label}</span>
                      <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{r.code}</span>
                    </div>
                  </td>
                  {kind === 'disposition' && (
                    <td>
                      {/* The group is what agents see it under, so it is changed here rather
                          than by retiring a code and making a new one in the right place. */}
                      {mayEdit ? (
                        <select className="arc-select arc-input-sm" aria-label={`Group for ${r.label}`}
                          value={r.kind ?? 'neutral'}
                          onChange={(e) => patch.mutate({ id: r.id, body: { kind: e.target.value } })}>
                          {DISPOSITION_GROUPS.map((g) => (
                            <option key={g} value={g}>{DISPOSITION_GROUP_LABELS[g]}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ fontSize: 12.5 }}>
                          {DISPOSITION_GROUP_LABELS[(r.kind ?? 'neutral') as DispositionGroup]}
                        </span>
                      )}
                    </td>
                  )}
                  {kind === 'disposition' && (
                    <td>
                      {mayEdit ? (
                        <select className="arc-select arc-input-sm" aria-label={`In a campaign, ${r.label}`}
                          value={r.effect ?? 'final'}
                          onChange={(e) => patch.mutate({ id: r.id, body: { effect: e.target.value } })}>
                          {(Object.keys(EFFECT_LABELS) as Array<DispositionCode['effect']>).map((k) => (
                            <option key={k} value={k}>{EFFECT_LABELS[k]}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ fontSize: 12.5 }}>{EFFECT_LABELS[r.effect ?? 'final']}</span>
                      )}
                    </td>
                  )}
                  <td>
                    {mayEdit ? (
                      <Switch id={`flag-${r.id}`}
                        checked={kind === 'pause' ? r.productive : !!r.requires_note}
                        onChange={(v) => patch.mutate({ id: r.id,
                          body: kind === 'pause' ? { productive: v } : { requires_note: v } })} />
                    ) : (
                      <span style={{ fontSize: 12.5 }}>
                        {(kind === 'pause' ? r.productive : r.requires_note) ? 'yes' : 'no'}
                      </span>
                    )}
                  </td>
                  <td>
                    {mayEdit ? (
                      <Switch id={`active-${r.id}`} checked={r.is_active}
                        onChange={(v) => patch.mutate({ id: r.id, body: { is_active: v } })} />
                    ) : (
                      <Badge tone={r.is_active ? 'ok' : 'muted'}>{r.is_active ? 'yes' : 'retired'}</Badge>
                    )}
                  </td>
                  <td className="arc-td-right">
                    {mayEdit && (
                      <RowAction kind="delete" label={`Remove ${r.label}`}
                        disabled={remove.isPending}
                        onClick={() => {
                          void confirmAction({
                            title: `Remove "${r.label}"?`,
                            body: 'If it has been used it is kept on old records and hidden from agents.',
                            action: 'Remove', danger: true,
                          }).then((ok) => { if (ok) remove.mutate(r.id); });
                        }} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {list.data !== undefined && rows.length === 0 && (
        <Empty icon="clock" title={`No ${title.toLowerCase()} yet`}
          hint="Add the words this account uses, and agents see them straight away." />
      )}
    </div>
  );
}
